import { JSONContent } from './useEditor';

const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

export function autolinkEmailsInJSON(node: JSONContent): JSONContent | JSONContent[] {
  // Only process text nodes
  if (node.type === 'text' && typeof node.text === 'string') {
    emailRegex.lastIndex = 0;
    let match: RegExpExecArray | null = emailRegex.exec(node.text);
    let lastIndex = 0;
    const parts: JSONContent[] = [];

    while (match) {
      // Add text before the email
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          text: node.text.slice(lastIndex, match.index),
          marks: node.marks,
        });
      }
      // Add the email with the mark
      parts.push({
        type: 'text',
        text: match[0],
        marks: [...(node.marks || []), { type: 'link', attrs: { href: `mailto:${match[0]}` } }],
      });
      lastIndex = match.index + match[0].length;
      match = emailRegex.exec(node.text);
    }
    // Add remaining text
    if (lastIndex < node.text.length) {
      parts.push({
        type: 'text',
        text: node.text.slice(lastIndex),
        marks: node.marks,
      });
    }
    // If no emails found, return original node
    if (parts.length === 1 && parts[0].text === node.text) {
      return node;
    }
    return parts;
  }

  // Recursively process children
  if (Array.isArray(node.content)) {
    const newContent = node.content.map(autolinkEmailsInJSON).flat();
    return { ...node, content: newContent };
  }

  return node;
}

export function addHighlightOnTextMatch(
  node: JSONContent,
  query: string
): JSONContent | JSONContent[] {
  if (!query || typeof query !== 'string' || query.length === 0) {
    return node;
  }
  // Only process text nodes
  if (node.type === 'text' && typeof node.text === 'string') {
    const { text } = node;
    const parts: JSONContent[] = [];
    let lastIndex = 0;
    const regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    let match: RegExpExecArray | null = regex.exec(text);
    while (match !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          text: text.slice(lastIndex, match.index),
          marks: node.marks,
        });
      }
      // Add the matched text with highlight mark
      parts.push({
        type: 'text',
        text: match[0],
        marks: [...(node.marks || []), { type: 'highlight' }],
      });
      lastIndex = match.index + match[0].length;
      match = regex.exec(text);
    }
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        text: text.slice(lastIndex),
        marks: node.marks,
      });
    }
    // If no matches, return original node
    if (parts.length === 1 && parts[0].text === text) {
      return node;
    }
    return parts;
  }
  // Recursively process children
  if (Array.isArray(node.content)) {
    const newContent = node.content.map((child) => addHighlightOnTextMatch(child, query)).flat();
    return { ...node, content: newContent };
  }
  return node;
}

type FormattingType = {
  hasRichText: boolean;
  formattingTypes: string[];
};

export const analyzeDocumentFormatting = (
  content: JSONContent | null | undefined
): FormattingType => {
  if (!content || !content.content) {
    return {
      hasRichText: false,
      formattingTypes: [],
    };
  }

  const formattingTypes = new Set<string>();
  let hasRichText = false;

  const traverseNode = (node: JSONContent) => {
    // Check for marks (bold, italic, etc.)
    if (node.marks && node.marks.length > 0) {
      hasRichText = true;
      node.marks.forEach((mark) => {
        formattingTypes.add(mark.type);
      });
    }

    // Check for specific node types
    if (node.type) {
      formattingTypes.add(node.type);
    }

    // Recursively check children
    if (node.content) {
      node.content.forEach(traverseNode);
    }
  };

  content.content.forEach(traverseNode);

  return {
    hasRichText,
    formattingTypes: Array.from(formattingTypes),
  };
};
